import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, 
  FileSpreadsheet, 
  Calculator, 
  Save, 
  History, 
  RefreshCw, 
  FileText,
  Trash2,
  Eye,
  X,
  AlertTriangle,
  UserPlus,
  Send,
  MessageCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Clientes definidos
const CLIENTES = ['CBA', 'SER', 'YCR', 'PROMO', 'PRORA', 'PROSE'];

// Clientes que tienen WhatsApp configurado
const CLIENTES_CON_WHATSAPP = ['PROMO', 'PROSE', 'PRORA', 'YCR'];

export default function CierreSemanal() {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [archivo, setArchivo] = useState(null);
  const [datosParseados, setDatosParseados] = useState(null);
  const [semanaInfo, setSemanaInfo] = useState({ numero: '', fechaDesde: '', fechaHasta: '' });
  
  // Jugadores sin asignar
  const [jugadoresSinAsignar, setJugadoresSinAsignar] = useState([]);
  
  // Valores manuales
  const [overlayTotal, setOverlayTotal] = useState(0);
  const [jackpotTotal, setJackpotTotal] = useState(0);
  const [devolucionTotal, setDevolucionTotal] = useState(0);
  
  // Historial
  const [historico, setHistorico] = useState([]);
  const [showHistorico, setShowHistorico] = useState(false);
  const [enviandoCierre, setEnviandoCierre] = useState({});
  const [cierreGuardadoId, setCierreGuardadoId] = useState(null);
  
  // Tasa de conversion PP a ARS
  const TASA_PP_ARS = 300;
  
  const fileInputRef = useRef(null);

  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0.00';
    return new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  useEffect(() => {
    cargarHistorico();
  }, []);

  const cargarHistorico = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/poker/cierre-semanal/historico`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setHistorico(res.data.cierres || []);
      }
    } catch (err) {
      console.error('Error cargando historial:', err);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Solo se permiten archivos Excel (.xlsx, .xls)');
        return;
      }
      setArchivo(file);
      toast.success(`Archivo seleccionado: ${file.name}`);
    }
  };

  const subirYParsear = async () => {
    if (!archivo) {
      toast.error('Selecciona un archivo Excel primero');
      return;
    }

    // Reset preventivo: limpiar state previo antes de parsear uno nuevo
    // Evita que queden datos de un upload anterior mezclados con el nuevo
    setDatosParseados(null);
    setJugadoresSinAsignar([]);
    setCierreGuardadoId(null);

    setUploading(true);
    const formData = new FormData();
    formData.append('file', archivo);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/poker/cierre-semanal/upload-v2`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      if (res.data.success) {
        setDatosParseados(res.data.datos);
        setJugadoresSinAsignar(res.data.jugadores_sin_asignar || []);
        setSemanaInfo({
          numero: res.data.semana_numero || '',
          fechaDesde: res.data.fecha_desde || '',
          fechaHasta: res.data.fecha_hasta || ''
        });
        
        if (res.data.jugadores_sin_asignar?.length > 0) {
          toast.warning(`Hay ${res.data.jugadores_sin_asignar.length} jugadores sin asignar`);
        } else {
          toast.success('Archivo parseado correctamente');
        }
      } else {
        toast.error(res.data.message || 'Error al parsear');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir archivo');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  // Asignar jugador a un cliente
  const asignarJugador = (index, clienteDestino) => {
    const jugador = jugadoresSinAsignar[index];
    
    if (!clienteDestino) {
      // Desasignar
      if (jugador.asignado_a) {
        // Restar del cliente anterior
        setDatosParseados(prev => ({
          ...prev,
          [jugador.asignado_a]: {
            ...prev[jugador.asignado_a],
            ganancias: prev[jugador.asignado_a].ganancias - jugador.ganancias,
            cuota: prev[jugador.asignado_a].cuota - jugador.cuota,
            spinup_3pct: prev[jugador.asignado_a].spinup_3pct - jugador.spinup_3pct,
            t_opon_cuota: prev[jugador.asignado_a].t_opon_cuota - jugador.t_opon_cuota,
            jugadores: prev[jugador.asignado_a].jugadores - 1
          }
        }));
      }
    } else {
      // Asignar a nuevo cliente
      if (jugador.asignado_a && jugador.asignado_a !== clienteDestino) {
        // Primero restar del cliente anterior
        setDatosParseados(prev => ({
          ...prev,
          [jugador.asignado_a]: {
            ...prev[jugador.asignado_a],
            ganancias: prev[jugador.asignado_a].ganancias - jugador.ganancias,
            cuota: prev[jugador.asignado_a].cuota - jugador.cuota,
            spinup_3pct: prev[jugador.asignado_a].spinup_3pct - jugador.spinup_3pct,
            t_opon_cuota: prev[jugador.asignado_a].t_opon_cuota - jugador.t_opon_cuota,
            jugadores: prev[jugador.asignado_a].jugadores - 1
          }
        }));
      }
      
      // Sumar al nuevo cliente
      if (jugador.asignado_a !== clienteDestino) {
        setDatosParseados(prev => ({
          ...prev,
          [clienteDestino]: {
            ...prev[clienteDestino],
            ganancias: prev[clienteDestino].ganancias + jugador.ganancias,
            cuota: prev[clienteDestino].cuota + jugador.cuota,
            spinup_3pct: prev[clienteDestino].spinup_3pct + jugador.spinup_3pct,
            t_opon_cuota: prev[clienteDestino].t_opon_cuota + jugador.t_opon_cuota,
            jugadores: prev[clienteDestino].jugadores + 1
          }
        }));
      }
    }
    
    // Actualizar el estado del jugador
    setJugadoresSinAsignar(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], asignado_a: clienteDestino || null };
      return updated;
    });
    
    if (clienteDestino) {
      toast.success(`${jugador.apodo || jugador.id} asignado a ${clienteDestino}`);
    }
  };

  // Calcular bonificacion escalonada segun perdida en ARS (min 7%, max 12%)
  const calcularBonificacion = (totalARS) => {
    if (totalARS >= 0) return 0; // Solo para perdedores
    
    const perdidaARS = Math.abs(totalARS);
    let porcentaje = 0;
    
    if (perdidaARS > 1000000) {
      porcentaje = 0.12; // 12% (máximo)
    } else if (perdidaARS > 500000) {
      porcentaje = 0.10; // 10%
    } else if (perdidaARS > 0) {
      porcentaje = 0.07; // 7% (mínimo)
    }
    
    return perdidaARS * porcentaje; // Retorna valor positivo
  };

  // Calcular distribucion proporcional de valores manuales
  const calcularDistribucion = () => {
    if (!datosParseados) return {};
    
    // Cuota total para overlay y jackpot (distribucion proporcional a cuota)
    const cuotaTotal = Object.values(datosParseados).reduce((sum, d) => sum + (d.cuota || 0), 0);
    
    // Calcular perdidas totales (solo clientes con t_opon_cuota negativo)
    // La devolucion se distribuye proporcionalmente a las perdidas
    let perdidasTotales = 0;
    CLIENTES.forEach(cliente => {
      const datos = datosParseados[cliente] || {};
      const resultado = datos.t_opon_cuota || 0;
      if (resultado < 0) {
        perdidasTotales += Math.abs(resultado); // Sumamos el valor absoluto de las perdidas
      }
    });
    
    const distribucion = {};
    
    CLIENTES.forEach(cliente => {
      const datos = datosParseados[cliente] || { cuota: 0, t_opon_cuota: 0 };
      const proporcionCuota = cuotaTotal > 0 ? (datos.cuota / cuotaTotal) : 0;
      const resultado = datos.t_opon_cuota || 0;
      
      // Devolucion: solo si el resultado es negativo, proporcional a cuanto perdio
      let devolucion = 0;
      if (resultado < 0 && perdidasTotales > 0) {
        const proporcionPerdida = Math.abs(resultado) / perdidasTotales;
        devolucion = devolucionTotal * proporcionPerdida;
      }
      
      distribucion[cliente] = {
        overlay: overlayTotal * proporcionCuota,
        jackpot: jackpotTotal * proporcionCuota,
        devolucion: devolucion,
        admin17: datos.cuota * 0.17 * -1 // 17% negativo
      };
    });
    
    return distribucion;
  };

  // Calcular totales finales por cliente
  const calcularTotales = () => {
    if (!datosParseados) return {};
    
    const distribucion = calcularDistribucion();
    const totales = {};
    
    CLIENTES.forEach(cliente => {
      const datos = datosParseados[cliente] || {};
      const dist = distribucion[cliente] || {};
      
      // Total PP sin bonificacion
      const totalPPSinBonif = (datos.t_opon_cuota || 0) + 
                      (datos.spinup_3pct || 0) + 
                      (datos.ticket || 0) + 
                      (dist.overlay || 0) + 
                      (dist.jackpot || 0) + 
                      (dist.admin17 || 0) + 
                      (dist.devolucion || 0);
      
      const totalARSSinBonif = totalPPSinBonif * TASA_PP_ARS;
      
      // Calcular bonificacion sobre el total ARS
      const bonificacionARS = calcularBonificacion(totalARSSinBonif);
      const bonificacionPP = bonificacionARS / TASA_PP_ARS;
      
      // Total final con bonificacion
      const totalPP = totalPPSinBonif + bonificacionPP;
      const totalARS = totalARSSinBonif + bonificacionARS;
      
      totales[cliente] = {
        ganancias: datos.ganancias || 0,
        cuota: datos.cuota || 0,
        t_opon_cuota: datos.t_opon_cuota || 0,
        spinup_3pct: datos.spinup_3pct || 0,
        ticket: datos.ticket || 0,
        overlay: dist.overlay || 0,
        jackpot: dist.jackpot || 0,
        admin17: dist.admin17 || 0,
        devolucion: dist.devolucion || 0,
        bonificacionPP,
        bonificacionARS,
        totalPP,
        totalARS,
        jugadores: datos.jugadores || 0
      };
    });
    
    return totales;
  };

  const guardarCierre = async () => {
    if (!datosParseados) {
      toast.error('No hay datos para guardar');
      return;
    }
    
    if (!semanaInfo.numero || !semanaInfo.fechaDesde || !semanaInfo.fechaHasta) {
      toast.error('Completa la informacion de la semana');
      return;
    }

    // Lock anti doble-click (evita guardar 2 veces el mismo payload)
    if (loading) {
      toast.warning('Ya se esta guardando, espera...');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const totales = calcularTotales();
      
      const res = await axios.post(`${API}/poker/cierre-semanal/guardar`, {
        semana_numero: semanaInfo.numero,
        fecha_desde: semanaInfo.fechaDesde,
        fecha_hasta: semanaInfo.fechaHasta,
        overlay_total: overlayTotal,
        jackpot_total: jackpotTotal,
        devolucion_total: devolucionTotal,
        datos_clientes: totales,
        archivo_nombre: archivo?.name || 'manual'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        toast.success('Cierre guardado correctamente');
        cargarHistorico();
        setArchivo(null);
        setDatosParseados(null);
        setOverlayTotal(0);
        setJackpotTotal(0);
        setDevolucionTotal(0);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const descargarPDF = async (cierreId, cliente) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/poker/cierre-semanal/${cierreId}/pdf-pro/${cliente}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cierre_semanal_${cliente}_${cierreId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`PDF descargado para ${cliente}`);
    } catch (err) {
      toast.error('Error al descargar PDF');
    }
  };

  const descargarPDFResumen = async (cierreId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/poker/cierre-semanal/${cierreId}/pdf-resumen`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cierre_semanal_RESUMEN_${cierreId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF Resumen descargado');
    } catch (err) {
      toast.error('Error al descargar PDF Resumen');
    }
  };

  const eliminarCierre = async (cierreId) => {
    if (!window.confirm('Estas seguro de eliminar este cierre?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/poker/cierre-semanal/${cierreId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cierre eliminado');
      cargarHistorico();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  // Enviar cierre por WhatsApp y agendar movimiento financiero
  const enviarCierreWhatsApp = async (cliente, cierreId) => {
    try {
      setEnviandoCierre(prev => ({ ...prev, [cliente]: true }));
      
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/poker/cierre-semanal/${cierreId}/enviar-cierre/${cliente}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success(
          `✅ Enviado a ${cliente}! Nuevo saldo: $${res.data.nuevo_saldo?.toLocaleString("es-AR") || "N/A"} ARS`,
          { duration: 5000 }
        );
      } else {
        toast.error(res.data.message || "Error al enviar");
      }
    } catch (err) {
      console.error("Error enviando cierre:", err);
      toast.error(err.response?.data?.detail || "Error al enviar por WhatsApp");
    } finally {
      setEnviandoCierre(prev => ({ ...prev, [cliente]: false }));
    }
  };

  const totales = calcularTotales();
  const granTotal = Object.values(totales).reduce((sum, t) => sum + (t.totalPP || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg">
              <Calculator className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Cierre Semanal
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                PPPoker - Liquidacion por cliente
              </p>
            </div>
          </div>
          
          <button
            onClick={() => setShowHistorico(!showHistorico)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            <History className="w-4 h-4" />
            Historial
          </button>
        </div>

        {/* Modal Historial */}
        {showHistorico && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold dark:text-white">Historial de Cierres</h2>
                <button onClick={() => setShowHistorico(false)}>
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              
              {historico.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No hay cierres registrados</p>
              ) : (
                <div className="space-y-3">
                  {historico.map((cierre) => (
                    <div key={cierre.id} className="border dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold dark:text-white">
                            Semana {cierre.semana_numero} | {cierre.fecha_desde} - {cierre.fecha_hasta}
                          </p>
                          <p className="text-sm text-gray-500">
                            Total: ${formatNumber(cierre.total_general)} PP
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => eliminarCierre(cierre.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Botones PDF */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        {CLIENTES.map(cliente => (
                          <button
                            key={cliente}
                            onClick={() => descargarPDF(cierre.id, cliente)}
                            className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 flex items-center gap-1"
                          >
                            <FileText className="w-3 h-3" />
                            PDF {cliente}
                          </button>
                        ))}
                        <button
                          onClick={() => descargarPDFResumen(cierre.id)}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1 font-semibold"
                        >
                          <FileText className="w-3 h-3" />
                          📊 RESUMEN GENERAL
                        </button>
                      </div>
                      
                      {/* Botones Enviar WhatsApp + Agendar */}
                      <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t dark:border-gray-600">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center mr-2">
                          <MessageCircle className="w-3 h-3 mr-1" />
                          Enviar y Agendar:
                        </span>
                        {CLIENTES_CON_WHATSAPP.map(cliente => (
                          <button
                            key={`wa-${cliente}`}
                            onClick={() => enviarCierreWhatsApp(cliente, cierre.id)}
                            disabled={enviandoCierre[cliente]}
                            className="px-3 py-1 text-xs bg-emerald-500 text-white rounded hover:bg-emerald-600 flex items-center gap-1 disabled:opacity-50"
                            title={`Enviar a ${cliente} por WhatsApp`}
                          >
                            {enviandoCierre[cliente] ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Send className="w-3 h-3" />
                            )}
                            {cliente}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Seccion de Carga */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 dark:text-white flex items-center gap-2">
            <Upload className="w-5 h-5 text-green-500" />
            Cargar Excel de PPPoker
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Semana Nro.
              </label>
              <input
                type="text"
                value={semanaInfo.numero}
                onChange={(e) => setSemanaInfo({...semanaInfo, numero: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Ej: 51"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fecha Desde
              </label>
              <input
                type="date"
                value={semanaInfo.fechaDesde}
                onChange={(e) => setSemanaInfo({...semanaInfo, fechaDesde: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fecha Hasta
              </label>
              <input
                type="date"
                value={semanaInfo.fechaHasta}
                onChange={(e) => setSemanaInfo({...semanaInfo, fechaHasta: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".xlsx,.xls"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-green-500 transition"
            >
              <FileSpreadsheet className="w-5 h-5 text-gray-500" />
              <span className="text-gray-600 dark:text-gray-300">
                {archivo ? archivo.name : 'Seleccionar archivo Excel'}
              </span>
            </button>
            
            <button
              onClick={subirYParsear}
              disabled={!archivo || uploading}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Parsear Excel
            </button>
          </div>
        </div>

        {/* Jugadores Sin Asignar */}
        {jugadoresSinAsignar.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl shadow-sm border border-amber-300 dark:border-amber-700 p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 text-amber-800 dark:text-amber-200 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Jugadores Sin Asignar ({jugadoresSinAsignar.length})
            </h2>
            <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
              Los siguientes jugadores no tienen agente asignado. Selecciona un cliente para cada uno:
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 dark:bg-amber-800">
                    <th className="px-3 py-2 text-left font-semibold">ID</th>
                    <th className="px-3 py-2 text-left font-semibold">Apodo</th>
                    <th className="px-3 py-2 text-left font-semibold">Agente Original</th>
                    <th className="px-3 py-2 text-right font-semibold">t.opon+cuota</th>
                    <th className="px-3 py-2 text-right font-semibold">Cuota</th>
                    <th className="px-3 py-2 text-center font-semibold">Asignar a</th>
                  </tr>
                </thead>
                <tbody>
                  {jugadoresSinAsignar.map((jugador, idx) => (
                    <tr key={idx} className={`border-b ${jugador.asignado_a ? 'bg-green-50 dark:bg-green-900/20' : 'bg-white dark:bg-gray-800'}`}>
                      <td className="px-3 py-2 font-mono text-xs">{jugador.id}</td>
                      <td className="px-3 py-2 font-semibold">{jugador.apodo || '-'}</td>
                      <td className="px-3 py-2 text-gray-500">{jugador.agente_original}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(jugador.t_opon_cuota)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(jugador.cuota)}</td>
                      <td className="px-3 py-2 text-center">
                        <select
                          value={jugador.asignado_a || ''}
                          onChange={(e) => asignarJugador(idx, e.target.value)}
                          className={`px-2 py-1 border rounded text-sm ${
                            jugador.asignado_a 
                              ? 'border-green-500 bg-green-100 dark:bg-green-800' 
                              : 'border-amber-400 bg-amber-50 dark:bg-amber-900'
                          }`}
                        >
                          <option value="">-- Seleccionar --</option>
                          {CLIENTES.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {jugadoresSinAsignar.some(j => !j.asignado_a) && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-3">
                ⚠️ Asigna todos los jugadores antes de guardar el cierre
              </p>
            )}
          </div>
        )}

        {/* Valores Manuales */}
        {datosParseados && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 dark:text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-orange-500" />
              Valores Manuales (se distribuyen proporcionalmente segun cuota)
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  OVERLAY Total
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={overlayTotal}
                  onChange={(e) => setOverlayTotal(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  JACKPOT Total
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={jackpotTotal}
                  onChange={(e) => setJackpotTotal(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  DEVOLUCION PERDIDAS Total
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={devolucionTotal}
                  onChange={(e) => setDevolucionTotal(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>
          </div>
        )}

        {/* Tabla de Resultados */}
        {datosParseados && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6 mb-6 overflow-x-auto">
            <h2 className="text-lg font-semibold mb-4 dark:text-white flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-500" />
              Resultados por Cliente
            </h2>
            
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="px-2 py-2 text-left font-semibold dark:text-white">Cliente</th>
                  <th className="px-2 py-2 text-right font-semibold dark:text-white">t.opon+cuota</th>
                  <th className="px-2 py-2 text-right font-semibold dark:text-white">SPINUP 3%</th>
                  <th className="px-2 py-2 text-right font-semibold dark:text-white">TICKET</th>
                  <th className="px-2 py-2 text-right font-semibold dark:text-white">OVERLAY</th>
                  <th className="px-2 py-2 text-right font-semibold dark:text-white">JACKPOT</th>
                  <th className="px-2 py-2 text-right font-semibold dark:text-white">17% Admin</th>
                  <th className="px-2 py-2 text-right font-semibold dark:text-white">DEVOL PER</th>
                  <th className="px-2 py-2 text-right font-semibold dark:text-white bg-amber-100 dark:bg-amber-900">BONIF</th>
                  <th className="px-2 py-2 text-right font-semibold dark:text-white bg-blue-100 dark:bg-blue-900">TOTAL PP</th>
                  <th className="px-2 py-2 text-right font-semibold dark:text-white bg-green-100 dark:bg-green-900">TOTAL ARS</th>
                </tr>
              </thead>
              <tbody>
                {CLIENTES.map((cliente, idx) => {
                  const t = totales[cliente] || {};
                  const colorClase = [
                    'bg-yellow-50 dark:bg-yellow-900/20',
                    'bg-blue-50 dark:bg-blue-900/20', 
                    'bg-orange-50 dark:bg-orange-900/20',
                    'bg-cyan-50 dark:bg-cyan-900/20',
                    'bg-green-50 dark:bg-green-900/20',
                    'bg-purple-50 dark:bg-purple-900/20'
                  ][idx];
                  
                  return (
                    <tr key={cliente} className={`${colorClase} border-b dark:border-gray-700`}>
                      <td className="px-2 py-2 font-bold dark:text-white">{cliente}</td>
                      <td className="px-2 py-2 text-right dark:text-gray-300">{formatNumber(t.t_opon_cuota)}</td>
                      <td className="px-2 py-2 text-right dark:text-gray-300">{formatNumber(t.spinup_3pct)}</td>
                      <td className="px-2 py-2 text-right dark:text-gray-300">{formatNumber(t.ticket)}</td>
                      <td className="px-2 py-2 text-right dark:text-gray-300">{formatNumber(t.overlay)}</td>
                      <td className="px-2 py-2 text-right dark:text-gray-300">{formatNumber(t.jackpot)}</td>
                      <td className="px-2 py-2 text-right text-red-600">{formatNumber(t.admin17)}</td>
                      <td className="px-2 py-2 text-right dark:text-gray-300">{formatNumber(t.devolucion)}</td>
                      <td className="px-2 py-2 text-right text-amber-600 font-semibold bg-amber-50 dark:bg-amber-900/30">{t.bonificacionPP > 0 ? '+' : ''}{formatNumber(t.bonificacionPP)}</td>
                      <td className="px-2 py-2 text-right font-bold bg-blue-100 dark:bg-blue-900 dark:text-white">{formatNumber(t.totalPP)}</td>
                      <td className="px-2 py-2 text-right font-bold bg-green-100 dark:bg-green-900 dark:text-white">{formatNumber(t.totalARS)}</td>

                    </tr>
                  );
                })}
                
                {/* Fila de totales */}
                <tr className="bg-gray-200 dark:bg-gray-600 font-bold">
                  <td className="px-2 py-2 dark:text-white">TOTAL</td>
                  <td className="px-2 py-2 text-right dark:text-white">
                    {formatNumber(Object.values(totales).reduce((s, t) => s + (t.t_opon_cuota || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right dark:text-white">
                    {formatNumber(Object.values(totales).reduce((s, t) => s + (t.spinup_3pct || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right dark:text-white">
                    {formatNumber(Object.values(totales).reduce((s, t) => s + (t.ticket || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right dark:text-white">{formatNumber(overlayTotal)}</td>
                  <td className="px-2 py-2 text-right dark:text-white">{formatNumber(jackpotTotal)}</td>
                  <td className="px-2 py-2 text-right text-red-600 dark:text-red-400">
                    {formatNumber(Object.values(totales).reduce((s, t) => s + (t.admin17 || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right dark:text-white">{formatNumber(devolucionTotal)}</td>
                  <td className="px-2 py-2 text-right text-amber-600 bg-amber-100 dark:bg-amber-800">
                    +{formatNumber(Object.values(totales).reduce((s, t) => s + (t.bonificacionPP || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right bg-blue-200 dark:bg-blue-800 dark:text-white">{formatNumber(granTotal)}</td>
                  <td className="px-2 py-2 text-right bg-green-200 dark:bg-green-800 dark:text-white">{formatNumber(granTotal * TASA_PP_ARS)}</td>
                </tr>
              </tbody>
            </table>
            
            {/* Boton Guardar */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={guardarCierre}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 shadow-lg"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                Guardar Cierre Semanal
              </button>
            </div>
          </div>
        )}

        {/* Mensaje inicial */}
        {!datosParseados && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-12 text-center">
            <FileSpreadsheet className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">
              Sin datos cargados
            </h3>
            <p className="text-gray-500 dark:text-gray-500">
              Sube un archivo Excel de PPPoker para comenzar el cierre semanal
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
